import type { ILog } from '../../interface/log';
import host from './host';

export interface IRegistrationConfig {
    scope?: string;
    onRegister?: (registration: ServiceWorkerRegistration) => void;
    onReady?: (worker: ServiceWorker) => void;
    onInstall?: (worker: ServiceWorker) => void;
}

const _hasServiceWorker = () => 'serviceWorker' in navigator;

const _fetchServiceWorker = (swUrl: string) => {
    return fetch(swUrl, {
        headers: {
            'Service-Worker': 'script',
        },
    });
};

interface IServiceWorkerRegisterable {
    isValid: boolean;
    description: string;
}
function _checkValidServiceWorker(swUrl: string): Promise<IServiceWorkerRegisterable> {
    return new Promise<IServiceWorkerRegisterable>(async function (resolve, reject) {
        try {
            const response = await _fetchServiceWorker(swUrl);

            const didFindFile = response?.status >= 200 && response?.status <= 299;
            const isJavascriptFile =
                (response?.headers?.get('content-type')?.indexOf('javascript') ?? -1) !== -1;

            const result: IServiceWorkerRegisterable = {
                isValid: false,
                description: '',
            };

            if (!didFindFile) {
                result.description = 'Unable to find service worker file';
            } else if (!isJavascriptFile) {
                result.description =
                    'Service worker has wrong mime type: ' +
                        response?.headers?.get('content-type') || 'unknown';
            } else {
                result.isValid = true;
            }

            resolve(result);
        } catch (error) {
            resolve({
                isValid: false,
                description: 'No internet connection found. App is running in offline mode.',
            });
        }
    });
}

function _canRegister(swUrl: string): Promise<boolean> {
    return new Promise(async function (resolve, reject) {
        try {
            let canRegister = false;

            if (host.isLocalHost()) {
                /* This is running on localhost. Let's check if a service worker still exists or not. */
                const { isValid } = await _checkValidServiceWorker(swUrl);
                canRegister = isValid;
            } else {
                /* Is not localhost. Just register service worker */
                canRegister = true;
            }

            resolve(canRegister);
        } catch (error) {
            reject(error);
        }
    });
}

function _registerWorker(
    log: ILog,
    swUrl: string,
    config?: IRegistrationConfig,
): Promise<ServiceWorkerRegistration> {
    const scope = config?.scope || '.';

    log.info(`Registering worker url:'${swUrl}' to scope:'${scope}'`);

    return navigator.serviceWorker.register(swUrl, { scope });
}

function _addRegistrationCallbacks(
    log: ILog,
    registration: ServiceWorkerRegistration,
    config?: IRegistrationConfig,
) {
    log.info('Registration completed, awaiting SW installed/activated status');

    function _processState(this: ServiceWorker) {
        switch (this.state) {
            case 'installing':
            case 'installed':
                log.info(`Worker is install(ed), ${this.state}`);
                config?.onInstall?.(this);
                break;

            case 'activating':
            case 'activated':
                log.info(`Worker is up and running, ${this.state}`);
                config?.onReady?.(this);
                break;

            default:
                log.info(`Worker state updated to: ${this.state}`);
                break;
        }
    }

    const onRegistrationUpdate = () => {
        const newWorker = registration.installing;

        if (newWorker) {
            newWorker.onstatechange = _processState;
        }
    };

    registration.onupdatefound = onRegistrationUpdate;

    if (registration.waiting) {
        registration.waiting.onstatechange = _processState;
    }

    if (registration.installing) {
        registration.installing.onstatechange = _processState;
    }

    if (registration.active) {
        registration.active.onstatechange = _processState;
        registration
            .update()
            .then(_ => log.verbose('Registration updated'))
            .catch(error => log.error(`Update failed: ${error}`));

        _processState.call(registration.active);
    }

    /* run through now, incase we are already in final state */
}

async function start(log: ILog, swUrl: string, config?: IRegistrationConfig) {
    if (!_hasServiceWorker()) {
        log.error('Unable to register, no service worker');
        return;
    }

    const _registerCb = async function () {
        if (await _canRegister(swUrl)) {
            log.info(`Starting SW ${swUrl} registration`);
            _registerWorker(log, swUrl, config)
                .then(reg => {
                    config?.onRegister?.(reg);
                    _addRegistrationCallbacks(log, reg, config);
                })
                .catch(error =>
                    log.error(`Registering new worker failed: ${JSON.stringify(error)}`),
                );
        } else {
            log.warn(
                'No service worker found, Probably a different app. Will reload the page on SW unregister',
            );
            navigator.serviceWorker.ready
                .then(registration => registration.unregister())
                .then(_ => window.location.reload())
                .catch(error => log.error('Failed to unregister SW: ' + error));
        }
    };

    const hasDOMLoaded = document.readyState === 'complete';

    /* Delay, registration. Don't degrade the first visit experience. */
    hasDOMLoaded
        ? _registerCb().catch(error =>
              log.error(`Failed to register onload: ${JSON.stringify(error)}`),
          )
        : window.addEventListener('load', _registerCb);
}

function stop(log: ILog, registration: ServiceWorkerRegistration) {
    registration
        .unregister()
        .catch(error => log.error(`Failed to register onload: ${JSON.stringify(error)}`));
}

const module = {
    start,
    stop,
};

export default module;
